import { Paper, Stack, Typography } from '@mui/material';
import { useAtom } from 'jotai';
import { useMemo } from 'react';
import Markdown from 'react-markdown';
import { useLocation } from 'react-router';

import { CaseDetailCard } from '@/components/CaseDetailCard';
import MarkdownLink from '@/components/MarkdownLink';
import Message from '@/components/Message';
import type { SyntheticMessage } from '@/components/Message/Message.types';
import { MARKDOWN_REMARK_PLUGINS } from '@/constants/markdown.constants';
import { caseDetailsAtom } from '@/pages/Case/Case.store';

export const CaseDetailsTurbine = () => {
  const [caseDetails] = useAtom(caseDetailsAtom);
  const { ticket, soc_case_extra_details } = caseDetails || {};
  const { pathname } = useLocation();

  const investigationComments = useMemo(() => {
    if (!soc_case_extra_details?.investigation_comments_structured) {
      return [];
    }
    return soc_case_extra_details.investigation_comments_structured?.map(
      (comment, index): SyntheticMessage => ({
        ...comment,
        human_soc_requested: false,
        kanati_requested: false,
        kanati_response_visibility: null,
        body: comment.message,
        id: index,
        internal: true,
        ticket_id: ticket?.id || 0,
        updated_at: comment.created_at,
        user: {
          internal: true,
          name: comment.created_by
        }
      })
    );
  }, [soc_case_extra_details, ticket?.id]);

  return (
    <Stack spacing={2} direction={{ md: 'column', lg: 'row' }}>
      <CaseDetailCard title="Scope Ticket Details" style={{ flex: 1 }}>
        {!ticket && (
          <Typography sx={{ p: 2, fontStyle: 'italic' }}>
            No Scope ticket associated with this case.
          </Typography>
        )}
        {ticket && (
          <>
            <Typography color="textSecondary">Ticket Title:</Typography>
            <Typography sx={{ fontWeight: 'bold' }} variant="h4">
              {ticket?.title}
            </Typography>

            <Typography sx={{ pt: 4 }} color="textSecondary">
              Ticket Summary:
            </Typography>
            <Paper className="markdown-body" variant="outlined" sx={{ p: 2 }}>
              <Markdown
                disallowedElements={['img']}
                remarkPlugins={MARKDOWN_REMARK_PLUGINS}
                unwrapDisallowed
                components={{
                  a: MarkdownLink
                }}
              >
                {ticket?.summary || ''}
              </Markdown>
            </Paper>

            <Typography sx={{ pt: 4 }} color="textSecondary">
              Ticket Messages:
            </Typography>
            {ticket?.messages?.map((message) => (
              <Message key={message.id} message={message} />
            ))}
          </>
        )}
      </CaseDetailCard>
      {pathname.includes('/case-internal') && (
        <CaseDetailCard title="SOC Comments" style={{ flex: 1 }}>
          {investigationComments.length === 0 && (
            <Typography sx={{ p: 2, fontStyle: 'italic' }}>
              No SOC comments available.
            </Typography>
          )}
          {investigationComments?.map((comment) => (
            <Message key={comment.id} message={comment} />
          ))}
        </CaseDetailCard>
      )}
    </Stack>
  );
};
export default CaseDetailsTurbine;
